/**
 * Ecommerce Service
 * Handles orders, product categories, analytics, and revenue tracking
 */
import api from './api';

const ecommerceService = {
  // ==================== Analytics ====================
  /**
   * Get analytics overview
   */
  getAnalyticsOverview: async (params = {}) => {
    const response = await api.get('/ecommerce/analytics/overview', { params });
    return response.data;
  },

  /**
   * Get revenue by day
   */
  getRevenueByDay: async (params = {}) => {
    const response = await api.get('/ecommerce/analytics/revenue-by-day', { params });
    return response.data;
  },

  /**
   * Get sales metrics
   */
  getSalesMetrics: async (params = {}) => {
    const response = await api.get('/ecommerce/analytics/sales-metrics', { params });
    return response.data;
  },

  /**
   * Get top products
   */
  getTopProducts: async (params = {}) => {
    const response = await api.get('/ecommerce/analytics/top-products', { params });
    return response.data;
  },

  /**
   * Get conversion funnel
   */
  getConversionFunnel: async (params = {}) => {
    const response = await api.get('/ecommerce/analytics/conversion-funnel', { params });
    return response.data;
  },

  // ==================== Orders ====================
  /**
   * Get all orders
   */
  getOrders: async (params = {}) => {
    const response = await api.get('/ecommerce/orders', { params });
    return response.data;
  },

  /**
   * Get order by ID
   */
  getOrder: async (orderId) => {
    const response = await api.get(`/ecommerce/orders/${orderId}`);
    return response.data;
  },

  /**
   * Create order
   */
  createOrder: async (orderData) => {
    const response = await api.post('/ecommerce/orders', orderData);
    return response.data;
  },

  /**
   * Update order status
   */
  updateOrderStatus: async (orderId, status, notes = '') => {
    const response = await api.put(`/ecommerce/orders/${orderId}/status`, {
      status,
      notes
    });
    return response.data;
  },

  /**
   * Cancel order
   */
  cancelOrder: async (orderId, reason = '') => {
    const response = await api.post(`/ecommerce/orders/${orderId}/cancel`, { reason });
    return response.data;
  },

  /**
   * Refund order
   */
  refundOrder: async (orderId, refundData) => {
    const response = await api.post(`/ecommerce/orders/${orderId}/refund`, refundData);
    return response.data;
  },

  /**
   * Get order timeline
   */
  getOrderTimeline: async (orderId) => {
    const response = await api.get(`/ecommerce/orders/${orderId}/timeline`);
    return response.data;
  },

  // ==================== Product Categories ====================
  /**
   * Get product category groups
   */
  getProductCategories: async (params = {}) => {
    const response = await api.get('/ecommerce/product-categories', { params });
    return response.data;
  },

  /**
   * Get category by ID
   */
  getCategory: async (categoryId) => {
    const response = await api.get(`/ecommerce/product-categories/${categoryId}`);
    return response.data;
  },

  /**
   * Create product category
   */
  createCategory: async (categoryData) => {
    const response = await api.post('/ecommerce/product-categories', categoryData);
    return response.data;
  },

  /**
   * Update product category
   */
  updateCategory: async (categoryId, categoryData) => {
    const response = await api.put(`/ecommerce/product-categories/${categoryId}`, categoryData);
    return response.data;
  },

  /**
   * Delete product category
   */
  deleteCategory: async (categoryId) => {
    const response = await api.delete(`/ecommerce/product-categories/${categoryId}`);
    return response.data;
  },

  /**
   * Get category tree
   */
  getCategoryTree: async () => {
    const response = await api.get('/ecommerce/product-categories/tree');
    return response.data;
  },

  // ==================== Products ====================
  /**
   * Get products by category
   */
  getProductsByCategory: async (categoryId, params = {}) => {
    const response = await api.get(`/ecommerce/product-categories/${categoryId}/products`, { params });
    return response.data;
  },

  /**
   * Sync products from external platform
   */
  syncProducts: async (platformId) => {
    const response = await api.post(`/ecommerce/platforms/${platformId}/sync`);
    return response.data;
  },

  /**
   * Get sync status
   */
  getSyncStatus: async (platformId) => {
    const response = await api.get(`/ecommerce/platforms/${platformId}/sync-status`);
    return response.data;
  },

  // ==================== Inventory ====================
  /**
   * Get inventory levels
   */
  getInventory: async (params = {}) => {
    const response = await api.get('/ecommerce/inventory', { params });
    return response.data;
  },

  /**
   * Update inventory
   */
  updateInventory: async (productId, quantity, reason = '') => {
    const response = await api.put(`/ecommerce/inventory/${productId}`, {
      quantity,
      reason
    });
    return response.data;
  },

  /**
   * Get low stock alerts
   */
  getLowStockAlerts: async () => {
    const response = await api.get('/ecommerce/inventory/low-stock');
    return response.data;
  },

  // ==================== Shipping ====================
  /**
   * Get shipping methods
   */
  getShippingMethods: async () => {
    const response = await api.get('/ecommerce/shipping/methods');
    return response.data;
  },

  /**
   * Calculate shipping
   */
  calculateShipping: async (shippingData) => {
    const response = await api.post('/ecommerce/shipping/calculate', shippingData);
    return response.data;
  },

  /**
   * Create shipment
   */
  createShipment: async (orderId, shipmentData) => {
    const response = await api.post(`/ecommerce/orders/${orderId}/shipments`, shipmentData);
    return response.data;
  },

  /**
   * Track shipment
   */
  trackShipment: async (orderId, shipmentId) => {
    const response = await api.get(`/ecommerce/orders/${orderId}/shipments/${shipmentId}/track`);
    return response.data;
  }
};

export default ecommerceService;
